import LegalPage from "../components/LegalPage";

/** /terms — the terms of service, adapted from Automattic's (CC BY-SA). */
const TermsPage = () => (
  <LegalPage title="Terms of Service" updated="September 14, 2026">
    <p>
      These Terms of Service ("Terms") govern your access to and use of
      StreamLover — the streamlover.app website, the StreamLover mobile apps,
      and the services available through them (together, the "Services"). Please
      read them carefully: by using the Services, you agree to be bound by these
      Terms.
    </p>

    <h2>1. Who's who</h2>
    <p>
      The Services are operated by Splatboard LLC, a Delaware limited liability
      company ("Splatboard," "we," or "us"). "You" means anyone who uses the
      Services, with or without an account.
    </p>

    <h2>2. What StreamLover is</h2>
    <p>
      StreamLover is a home for live, human-picked internet radio. The stations
      and streams you can listen to through the Services are created and
      operated by independent broadcasters, not by us. We relay their streams,
      publish information about their shows and DJs, and — where a station has
      arranged it — make recordings of past broadcasts available. We don't
      control what stations broadcast, and a station or stream may change or
      disappear at any time.
    </p>
    <p>
      The Services are currently in beta and free to use. If we ever introduce
      paid features, we'll present their terms before you buy anything.
    </p>

    <h2>3. Your account</h2>
    <p>
      Some features require an account, which you create with your email
      address. You're responsible for keeping access to that email address
      secure and for all activity under your account. Provide accurate
      information, and let us know at{" "}
      <a href="mailto:legal@streamlover.app">legal@streamlover.app</a> if you
      believe your account has been compromised.
    </p>

    <h2>4. Minimum age</h2>
    <p>
      You must be at least 13 years old to use the Services. If you're under the
      age required to consent to these Terms where you live, a parent or legal
      guardian must agree to them on your behalf.
    </p>

    <h2>5. Your content</h2>
    <p>
      You can post content to the Services — comments, reactions, a profile. You
      own what you post, and you're responsible for it. By posting, you give us
      a worldwide, royalty-free license to store, display, reproduce, and
      distribute that content as needed to operate, promote, and improve the
      Services. This license ends for content you delete, except where it has
      already been shared with others or where we retain copies for legal or
      backup purposes.
    </p>
    <p>When you post, you agree that you won't:</p>
    <ul>
      <li>
        post content that is unlawful, defamatory, harassing, hateful, or that
        infringes anyone's rights, including copyright;
      </li>
      <li>impersonate any person or misrepresent your affiliation;</li>
      <li>post spam, malware, or deceptive links;</li>
      <li>collect other users' information without their consent.</li>
    </ul>
    <p>
      We may remove content or suspend accounts that we reasonably believe
      violate these Terms, but we have no obligation to monitor content and
      aren't responsible for what users post.
    </p>

    <h2>6. Copyright and DMCA</h2>
    <p>
      We respond to notices of alleged copyright infringement as described in
      our <a href="/dmca">DMCA policy</a>. We will terminate the accounts of
      repeat infringers in appropriate circumstances.
    </p>

    <h2>7. Third-party streams and services</h2>
    <p>
      Streams, station pages, and links available through the Services originate
      with third parties. We don't endorse them and aren't responsible for their
      content, accuracy, or practices. Your dealings with a broadcaster or any
      other third party are between you and them.
    </p>

    <h2>8. Our intellectual property</h2>
    <p>
      These Terms don't transfer any Splatboard or third-party intellectual
      property to you. The StreamLover name, logo, apps, and site are ours, and
      all rights in them are reserved.
    </p>

    <h2>9. Acceptable use</h2>
    <p>
      Don't interfere with the Services or use them in ways they aren't meant
      for. That includes probing or breaching security measures, scraping or
      bulk-downloading content, rebroadcasting streams obtained through the
      Services, overwhelming our infrastructure, or accessing the Services by
      any automated means that burdens them.
    </p>

    <h2>10. Termination</h2>
    <p>
      You can stop using the Services and delete your account at any time. We
      may suspend or terminate your access to all or part of the Services at any
      time, with or without cause or notice — for example, if you breach these
      Terms. Sections of these Terms that by their nature should survive
      termination (including sections 11 through 14) will survive.
    </p>

    <h2>11. Disclaimers</h2>
    <p>
      The Services are provided "as is" and "as available." To the fullest
      extent permitted by law, Splatboard disclaims all warranties, express or
      implied, including warranties of merchantability, fitness for a particular
      purpose, and non-infringement. We don't warrant that the Services will be
      uninterrupted, error-free, or secure, or that any stream will remain
      available.
    </p>

    <h2>12. Limitation of liability</h2>
    <p>
      To the fullest extent permitted by law, Splatboard will not be liable for
      any indirect, incidental, special, consequential, or punitive damages, or
      for lost profits, data, or goodwill, arising out of or related to the
      Services or these Terms. Our total liability for all claims will not
      exceed the greater of one hundred dollars ($100) and the amounts you paid
      us for the Services in the twelve months before the claim arose. Some
      jurisdictions don't allow certain limitations, so parts of this section
      may not apply to you.
    </p>

    <h2>13. Indemnification</h2>
    <p>
      You agree to indemnify and hold harmless Splatboard, its members,
      officers, employees, and agents from any claims, damages, and expenses
      (including reasonable attorneys' fees) arising from your content, your use
      of the Services, or your violation of these Terms.
    </p>

    <h2>14. Governing law and disputes</h2>
    <p>
      These Terms are governed by the laws of the State of Delaware, excluding
      its conflict-of-law rules. Before filing a claim, you agree to try to
      resolve the dispute informally by contacting us at{" "}
      <a href="mailto:legal@streamlover.app">legal@streamlover.app</a>. Any
      dispute that can't be resolved informally will be brought exclusively in
      the state or federal courts located in Delaware, and both you and
      Splatboard consent to their jurisdiction.
    </p>

    <h2>15. Changes to these Terms</h2>
    <p>
      We may update these Terms from time to time. If a change is material,
      we'll take reasonable steps to let you know — for example, a notice on the
      site or in the app. Continuing to use the Services after a change takes
      effect means you accept the updated Terms; if you don't agree, stop using
      the Services.
    </p>

    <h2>16. Miscellaneous</h2>
    <p>
      These Terms, together with the <a href="/privacy">Privacy Policy</a> and{" "}
      <a href="/dmca">DMCA policy</a>, are the entire agreement between you and
      Splatboard about the Services. If any provision is found unenforceable,
      the rest remain in effect. Our failure to enforce a provision isn't a
      waiver of it. You may not assign these Terms without our consent; we may
      assign them in connection with a merger, sale, or reorganization.
    </p>

    <h2>17. Contact</h2>
    <p>
      Questions about these Terms? Email{" "}
      <a href="mailto:legal@streamlover.app">legal@streamlover.app</a>.
    </p>

    <p className="legal__credit">
      Adapted from Automattic's{" "}
      <a href="https://github.com/Automattic/legalmattic">
        open-source legal documents
      </a>
      , available under a{" "}
      <a href="https://creativecommons.org/licenses/by-sa/4.0/">
        Creative Commons Attribution-ShareAlike 4.0
      </a>{" "}
      license. This document is likewise licensed under CC BY-SA 4.0.
    </p>
  </LegalPage>
);

export default TermsPage;
